/**
 * LeadPaintDisclosureCard — the parties' own answers for the federal
 * Lead-Based Paint Disclosure on an accepted deal for a home built before
 * 1978, or one whose year built isn't known (42 U.S.C. 4852d; 40 C.F.R.
 * 745.113; QA run 2026-10-19, findings D5/D6).
 *
 *   - Seller: whether they know of lead-based paint or hazards (and a
 *     description), whether they have records or reports (a list, and a
 *     confirmation the buyer gets copies), and that no agent works on the sale
 *     for them — the form has no agent's acknowledgment, so a seller with one
 *     is stopped here.
 *   - Buyer: a link to the EPA pamphlet and a confirmation they received it,
 *     plus the seller's answers to review before signing.
 *
 * The backend generates the disclosure once both are recorded and refuses to
 * send the agreement before then (send-readiness reasons). Nothing changes
 * once the agreement is sent. Renders nothing when `required` is false.
 *
 * Props:
 *   offerId  – the accepted offer
 *   onSaved  – (state) after either party saves, so readiness is re-checked
 *
 * Copy: legal review 2026-10-19 (real-estate-law-expert), with a "not legal
 * advice" disclaimer. The backend's SELLER_ATTESTATION_VERSION names this
 * wording; bump both together.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AGENT,
  HAZARDS,
  RECORDS,
  confirmPamphletReceipt,
  getLeadPaintDisclosure,
  saveSellerAnswers,
} from '../../agents/leadPaintDisclosureAgent';
import styles from './LeadPaintDisclosureCard.module.css';

export const MAX_ANSWER_LENGTH = 2000;

export const COPY = {
  heading: 'Lead-based paint disclosure',
  loading: 'Checking the lead-based paint disclosure…',
  loadError: "We couldn't load the lead-based paint disclosure for this deal.",
  retry: 'Retry',
  sellerIntro:
    "Your home was built before 1978, or we don't know when it was built. Federal law requires you to tell the buyer what you know about lead-based paint before the buyer is bound. Your answers go on the Lead-Based Paint Disclosure that you and the buyer sign. You don't have to test the home. Answer based on what you know.",
  hazardsQuestion: 'Do you know of any lead-based paint or lead-based paint hazards in the home?',
  hazardsKnown: 'Yes, I know of lead-based paint or lead hazards in the home.',
  hazardsNone: 'No, I have no knowledge of lead-based paint or lead hazards in the home.',
  hazardsHelp: 'If any test or report ever found lead-based paint in the home, answer Yes.',
  hazardsDescriptionLabel: 'Describe what you know',
  hazardsDescriptionHelp:
    'Describe what you know. Say where it is (for example, windows, trim, porch or siding). Say how you know (for example, a test, an inspection or past repair work). Describe the condition of the painted surfaces now.',
  recordsQuestion: 'Do you have any records or reports about lead-based paint or lead hazards in the home?',
  recordsProvided: 'Yes, I have records or reports.',
  recordsNone: 'No, I have no records or reports.',
  recordsListLabel: 'List each record or report',
  recordsListHelp:
    "List each one with its type, who prepared it, and its date. Example: 'Lead inspection report, ABC Lead Services, May 2019.' Include test results, inspection or risk assessment reports, and abatement or repair records. Include a past buyer's report if you received a copy. For a condo, include any records you have about the common areas.",
  recordsDelivery:
    'I have given, or will give, the buyer complete copies of every record and report listed above before the buyer signs. The disclosure will say I have provided them.',
  agentQuestion:
    "Is a real estate agent or broker working on this sale for you, or have you agreed to pay the buyer's agent?",
  agentNo: 'No',
  agentYes: 'Yes',
  agentBlocked:
    "Federal law requires your agent to sign an agent's acknowledgment, and this form doesn't include one. Ask your agent to provide the lead-based paint disclosure for this sale.",
  attest: 'My answers are true and complete to the best of my knowledge.',
  changeNote: 'You can change your answers until the disclosure is sent for signature.',
  submit: 'Save answers',
  saveChanges: 'Save changes',
  saving: 'Saving…',
  cancel: 'Cancel',
  edit: 'Edit',
  sellerSaveErrorFallback: "We couldn't save your answers. Please try again.",
  youAnswered: 'You answered the lead-based paint questions.',
  pamphletSentence:
    'Federal law requires that you receive the EPA pamphlet "Protect Your Family From Lead in Your Home" before you sign for a home that may have been built before 1978.',
  pamphletLink: 'Read or download the EPA pamphlet',
  pamphletCheckbox: 'I have received the EPA pamphlet "Protect Your Family From Lead in Your Home."',
  pamphletSubmit: 'Confirm receipt',
  pamphletSaveErrorFallback: "We couldn't record your confirmation. Please try again.",
  pamphletConfirmed: (date) => `You confirmed receiving the EPA pamphlet${date ? ` on ${date}` : ''}.`,
  buyerReviewHeading: "The seller's answers",
  buyerReviewNote:
    "You aren't bound until everyone signs. If anything here changes your mind, decline and send a new offer. You can also change your lead inspection choice.",
  sellerWaiting: 'Waiting for the seller to answer the lead-based paint questions.',
  buyerWaiting: 'Waiting for the buyer to confirm receiving the EPA pamphlet.',
  buyerDone: 'The buyer confirmed receiving the EPA pamphlet.',
  sellerDone: 'The seller answered the lead-based paint questions.',
  lockedNote: 'The agreement was sent, so these answers can no longer change.',
  summaryHazardsKnown: 'Knows of lead-based paint or hazards',
  summaryHazardsNone: 'No knowledge of lead-based paint or hazards',
  summaryRecordsProvided: 'Has records or reports',
  summaryRecordsNone: 'No records or reports',
  disclaimer:
    "This is general information, not legal advice. If you're unsure how to answer, talk to a real estate attorney.",
  errors: {
    hazards: 'Choose Yes or No.',
    hazardsDescription: 'Describe the lead-based paint or hazards you know of.',
    records: 'Choose Yes or No.',
    recordsDescription: 'List each record or report.',
    recordsDelivery: 'Confirm the buyer gets copies of every record listed.',
    agent: 'Choose Yes or No.',
    attest: 'Confirm your answers are true and complete.',
    tooLong: `Keep this to ${MAX_ANSWER_LENGTH} characters or fewer.`,
  },
};

const EMPTY_FORM = {
  hazards_answer: '',
  hazards_description: '',
  records_answer: '',
  records_description: '',
  records_delivery_confirmed: false,
  agent_answer: '',
  attested: false,
};

/** Field errors for the seller's form; empty when it can be sent. */
export const validateSellerAnswers = (form) => {
  const errors = {};
  if (form.hazards_answer !== HAZARDS.KNOWN && form.hazards_answer !== HAZARDS.NONE) {
    errors.hazards_answer = COPY.errors.hazards;
  } else if (form.hazards_answer === HAZARDS.KNOWN) {
    const text = form.hazards_description.trim();
    if (!text) errors.hazards_description = COPY.errors.hazardsDescription;
    else if (text.length > MAX_ANSWER_LENGTH) errors.hazards_description = COPY.errors.tooLong;
  }
  if (form.records_answer !== RECORDS.PROVIDED && form.records_answer !== RECORDS.NONE) {
    errors.records_answer = COPY.errors.records;
  } else if (form.records_answer === RECORDS.PROVIDED) {
    const text = form.records_description.trim();
    if (!text) errors.records_description = COPY.errors.recordsDescription;
    else if (text.length > MAX_ANSWER_LENGTH) errors.records_description = COPY.errors.tooLong;
    if (!form.records_delivery_confirmed) errors.records_delivery_confirmed = COPY.errors.recordsDelivery;
  }
  if (form.agent_answer === AGENT.AGENT) {
    errors.agent_answer = COPY.agentBlocked;
  } else if (form.agent_answer !== AGENT.NONE) {
    errors.agent_answer = COPY.errors.agent;
  }
  if (!form.attested) errors.attested = COPY.errors.attest;
  return errors;
};

const formFromRecord = (record) => (record?.seller_answered
  ? {
    hazards_answer: record.hazards_answer || '',
    hazards_description: record.hazards_description || '',
    records_answer: record.records_answer || '',
    records_description: record.records_description || '',
    records_delivery_confirmed: Boolean(record.records_delivery_confirmed),
    agent_answer: record.agent_answer || '',
    attested: false,
  }
  : EMPTY_FORM);

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

/** The seller's recorded answers, read-only (both parties see them). */
const AnswersSummary = ({ record }) => (
  <dl className={styles.details} data-testid="lead-paint-answers">
    <dt>Lead-based paint</dt>
    <dd>
      {record.hazards_answer === HAZARDS.KNOWN ? COPY.summaryHazardsKnown : COPY.summaryHazardsNone}
      {record.hazards_answer === HAZARDS.KNOWN && record.hazards_description && (
        <span className={styles.answerText}>{record.hazards_description}</span>
      )}
    </dd>
    <dt>Records and reports</dt>
    <dd>
      {record.records_answer === RECORDS.PROVIDED ? COPY.summaryRecordsProvided : COPY.summaryRecordsNone}
      {record.records_answer === RECORDS.PROVIDED && record.records_description && (
        <span className={styles.answerText}>{record.records_description}</span>
      )}
    </dd>
  </dl>
);

const RadioGroup = ({ name, legend, help, value, options, onChange, error, idBase }) => (
  <fieldset
    className={styles.fieldset}
    aria-describedby={[help && `${idBase}-${name}-help`, error && `${idBase}-${name}-error`]
      .filter(Boolean).join(' ') || undefined}
  >
    <legend className={styles.legend}>{legend}</legend>
    {help && <p id={`${idBase}-${name}-help`} className={styles.helper}>{help}</p>}
    <div className={styles.radioColumn}>
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`${styles.radioOption} ${value === opt.value ? styles.radioOptionSelected : ''}`}
        >
          <input
            type="radio"
            name={`${idBase}-${name}`}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            data-testid={`lead-paint-${name}-${opt.value}`}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
    {error && <p id={`${idBase}-${name}-error`} className={styles.fieldError} role="alert">{error}</p>}
  </fieldset>
);

const LeadPaintDisclosureCard = ({ offerId, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pamphletChecked, setPamphletChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const onSavedRef = useRef(onSaved);
  useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);

  const load = useCallback(async () => {
    if (offerId == null) return;
    setLoading(true);
    setError(null);
    try {
      setData((await getLeadPaintDisclosure(offerId)) || null);
    } catch (err) {
      setError(err?.message || COPY.loadError);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    let active = true;
    if (offerId == null) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    getLeadPaintDisclosure(offerId)
      .then((payload) => { if (active) setData(payload || null); })
      .catch((err) => { if (active) setError(err?.message || COPY.loadError); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [offerId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const applyResponse = (payload) => {
    setData(payload || null);
    onSavedRef.current?.(payload);
  };

  const handleWriteError = (err, fallback) => {
    const status = err?.status ?? err?.statusCode;
    if (status === 409) {
      setData((prev) => (prev ? { ...prev, locked: true } : prev));
      setEditing(false);
    }
    setSaveError(err?.message || fallback);
  };

  const handleSellerSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const errors = validateSellerAnswers(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      applyResponse(await saveSellerAnswers(offerId, {
        ...form,
        hazards_description: form.hazards_description.trim(),
        records_description: form.records_description.trim(),
      }));
      setEditing(false);
    } catch (err) {
      handleWriteError(err, COPY.sellerSaveErrorFallback);
    } finally {
      setSaving(false);
    }
  };

  const handlePamphletSubmit = async (e) => {
    e.preventDefault();
    if (saving || !pamphletChecked) return;
    setSaving(true);
    setSaveError(null);
    try {
      applyResponse(await confirmPamphletReceipt(offerId));
    } catch (err) {
      handleWriteError(err, COPY.pamphletSaveErrorFallback);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <section className={styles.card} data-testid="lead-paint-card" aria-busy="true">
        <p className={styles.muted} role="status">{COPY.loading}</p>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className={styles.card} data-testid="lead-paint-card">
        <div className={styles.error} role="alert">
          <span>{error}</span>
          <button type="button" className={styles.linkButton} onClick={load}>{COPY.retry}</button>
        </div>
      </section>
    );
  }

  if (!data?.required) return null;

  const idBase = `lead-paint-${offerId}`;
  const record = data.record || null;
  const locked = Boolean(data.locked);
  const isSeller = data.my_role === 'seller';
  const sellerAnswered = Boolean(record?.seller_answered);
  const pamphletReceived = Boolean(record?.pamphlet_received);
  const pamphlet = data.pamphlet || {};

  const startEdit = () => {
    setForm(formFromRecord(record));
    setFieldErrors({});
    setSaveError(null);
    setEditing(true);
  };

  const renderSellerForm = () => (
    <form className={styles.form} onSubmit={handleSellerSubmit} noValidate data-testid="lead-paint-seller-form">
      <RadioGroup
        idBase={idBase}
        name="hazards"
        legend={COPY.hazardsQuestion}
        help={COPY.hazardsHelp}
        value={form.hazards_answer}
        onChange={(v) => updateField('hazards_answer', v)}
        error={fieldErrors.hazards_answer}
        options={[
          { value: HAZARDS.KNOWN, label: COPY.hazardsKnown },
          { value: HAZARDS.NONE, label: COPY.hazardsNone },
        ]}
      />
      {form.hazards_answer === HAZARDS.KNOWN && (
        <div className={styles.field}>
          <label htmlFor={`${idBase}-hazards-text`} className={styles.label}>{COPY.hazardsDescriptionLabel}</label>
          <p id={`${idBase}-hazards-text-help`} className={styles.helper}>{COPY.hazardsDescriptionHelp}</p>
          <textarea
            id={`${idBase}-hazards-text`}
            className={styles.input}
            rows={4}
            maxLength={MAX_ANSWER_LENGTH}
            value={form.hazards_description}
            onChange={(e) => updateField('hazards_description', e.target.value)}
            aria-invalid={fieldErrors.hazards_description ? true : undefined}
            aria-describedby={`${idBase}-hazards-text-help`}
            data-testid="lead-paint-hazards-description"
          />
          {fieldErrors.hazards_description && (
            <p className={styles.fieldError} role="alert">{fieldErrors.hazards_description}</p>
          )}
        </div>
      )}

      <RadioGroup
        idBase={idBase}
        name="records"
        legend={COPY.recordsQuestion}
        value={form.records_answer}
        onChange={(v) => updateField('records_answer', v)}
        error={fieldErrors.records_answer}
        options={[
          { value: RECORDS.PROVIDED, label: COPY.recordsProvided },
          { value: RECORDS.NONE, label: COPY.recordsNone },
        ]}
      />
      {form.records_answer === RECORDS.PROVIDED && (
        <div className={styles.field}>
          <label htmlFor={`${idBase}-records-text`} className={styles.label}>{COPY.recordsListLabel}</label>
          <p id={`${idBase}-records-text-help`} className={styles.helper}>{COPY.recordsListHelp}</p>
          <textarea
            id={`${idBase}-records-text`}
            className={styles.input}
            rows={4}
            maxLength={MAX_ANSWER_LENGTH}
            value={form.records_description}
            onChange={(e) => updateField('records_description', e.target.value)}
            aria-invalid={fieldErrors.records_description ? true : undefined}
            aria-describedby={`${idBase}-records-text-help`}
            data-testid="lead-paint-records-description"
          />
          {fieldErrors.records_description && (
            <p className={styles.fieldError} role="alert">{fieldErrors.records_description}</p>
          )}
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={form.records_delivery_confirmed}
              onChange={(e) => updateField('records_delivery_confirmed', e.target.checked)}
              data-testid="lead-paint-records-delivery"
            />
            <span>{COPY.recordsDelivery}</span>
          </label>
          {fieldErrors.records_delivery_confirmed && (
            <p className={styles.fieldError} role="alert">{fieldErrors.records_delivery_confirmed}</p>
          )}
        </div>
      )}

      <RadioGroup
        idBase={idBase}
        name="agent"
        legend={COPY.agentQuestion}
        value={form.agent_answer}
        onChange={(v) => updateField('agent_answer', v)}
        error={fieldErrors.agent_answer}
        options={[
          { value: AGENT.NONE, label: COPY.agentNo },
          { value: AGENT.AGENT, label: COPY.agentYes },
        ]}
      />
      {form.agent_answer === AGENT.AGENT && !fieldErrors.agent_answer && (
        <p className={styles.blocked} role="status" data-testid="lead-paint-agent-blocked">{COPY.agentBlocked}</p>
      )}

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={form.attested}
          onChange={(e) => updateField('attested', e.target.checked)}
          data-testid="lead-paint-attest"
        />
        <span>{COPY.attest}</span>
      </label>
      {fieldErrors.attested && <p className={styles.fieldError} role="alert">{fieldErrors.attested}</p>}

      <p className={styles.helper}>{COPY.changeNote}</p>

      {saveError && <div className={styles.error} role="alert">{saveError}</div>}

      <div className={styles.actions}>
        <button
          type="submit"
          className={styles.primaryButton}
          disabled={saving || form.agent_answer === AGENT.AGENT}
          aria-busy={saving || undefined}
        >
          {saving ? COPY.saving : (sellerAnswered ? COPY.saveChanges : COPY.submit)}
        </button>
        {sellerAnswered && (
          <button type="button" className={styles.secondaryButton} onClick={() => setEditing(false)} disabled={saving}>
            {COPY.cancel}
          </button>
        )}
      </div>
    </form>
  );

  const renderSeller = () => (
    <>
      <p className={styles.intro}>{COPY.sellerIntro}</p>
      {(!sellerAnswered || editing) && !locked ? renderSellerForm() : (
        <div className={styles.summary} data-testid="lead-paint-seller-summary">
          {sellerAnswered && <span className={styles.checkIcon} aria-hidden="true">✓</span>}
          <div className={styles.summaryBody}>
            <p className={styles.summaryLine}>{sellerAnswered ? COPY.youAnswered : COPY.sellerWaiting}</p>
            {sellerAnswered && <AnswersSummary record={record} />}
          </div>
          {sellerAnswered && !locked && (
            <button type="button" className={styles.secondaryButton} onClick={startEdit}
              aria-label="Edit your lead-based paint answers">
              {COPY.edit}
            </button>
          )}
        </div>
      )}
      {!editing && saveError && <div className={styles.error} role="alert">{saveError}</div>}
      <p className={`${styles.otherParty} ${pamphletReceived ? styles.otherPartyDone : ''}`}
        data-testid="lead-paint-other">
        <span aria-hidden="true">{pamphletReceived ? '✓' : '⏳'}</span>{' '}
        {pamphletReceived ? COPY.buyerDone : COPY.buyerWaiting}
      </p>
    </>
  );

  const renderBuyer = () => (
    <>
      <p className={styles.intro}>{COPY.pamphletSentence}</p>
      <p className={styles.intro}>
        <a href={pamphlet.url} target="_blank" rel="noopener noreferrer" className={styles.link}
          data-testid="lead-paint-pamphlet-link">
          {COPY.pamphletLink}
        </a>
        {pamphlet.edition && <span className={styles.muted}> ({pamphlet.edition} edition, PDF)</span>}
      </p>
      {pamphletReceived ? (
        <div className={styles.summary} data-testid="lead-paint-pamphlet-confirmed">
          <span className={styles.checkIcon} aria-hidden="true">✓</span>
          <p className={styles.summaryLine}>{COPY.pamphletConfirmed(formatDate(record?.pamphlet_received_at))}</p>
        </div>
      ) : !locked && (
        <form className={styles.form} onSubmit={handlePamphletSubmit} noValidate>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={pamphletChecked}
              onChange={(e) => setPamphletChecked(e.target.checked)}
              data-testid="lead-paint-pamphlet-checkbox"
            />
            <span>{COPY.pamphletCheckbox}</span>
          </label>
          {saveError && <div className={styles.error} role="alert">{saveError}</div>}
          <div className={styles.actions}>
            <button type="submit" className={styles.primaryButton} disabled={saving || !pamphletChecked}
              aria-busy={saving || undefined}>
              {saving ? COPY.saving : COPY.pamphletSubmit}
            </button>
          </div>
        </form>
      )}

      <h4 className={styles.subheading}>{COPY.buyerReviewHeading}</h4>
      {sellerAnswered ? (
        <>
          <AnswersSummary record={record} />
          <p className={styles.helper}>{COPY.buyerReviewNote}</p>
        </>
      ) : (
        <p className={styles.otherParty} data-testid="lead-paint-other">
          <span aria-hidden="true">⏳</span> {COPY.sellerWaiting}
        </p>
      )}
    </>
  );

  return (
    <section className={styles.card} aria-labelledby={`${idBase}-heading`} data-testid="lead-paint-card">
      <h3 id={`${idBase}-heading`} className={styles.heading}>{COPY.heading}</h3>
      {isSeller ? renderSeller() : renderBuyer()}
      {locked && <p className={styles.helper} data-testid="lead-paint-locked">{COPY.lockedNote}</p>}
      <p className={styles.disclaimer}>{COPY.disclaimer}</p>
    </section>
  );
};

export default LeadPaintDisclosureCard;
